import React, { useState, createContext, useContext } from 'react';
import { ChevronDown } from 'lucide-react';

const SelectContext = createContext();

export const Select = ({ children, value, onValueChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <SelectContext.Provider value={{ value, onValueChange, open, setOpen }}>
      <div className="relative w-full">
        {children}
      </div>
    </SelectContext.Provider>
  );
};

export const SelectTrigger = React.forwardRef(
  ({ className = '', children, ...props }, ref) => {
    const context = useContext(SelectContext);
    
    if (!context) {
      throw new Error('SelectTrigger must be used within Select');
    }

      const { open, setOpen } = context;

    return (
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between bg-white ${className}`}
        {...props}
      >
        {children}
        <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
    );
  }
);

SelectTrigger.displayName = 'SelectTrigger';

export const SelectValue = ({ placeholder = 'Select an option' }) => {
  const context = useContext(SelectContext);
  
  if (!context) {
    return placeholder;
  }

  return context.value || placeholder;
};

export const SelectContent = ({ children, className = '' }) => {
  const context = useContext(SelectContext);
  
  if (!context || !context.open) return null;

  return (
    <div className={`absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto ${className}`}>
      {children}
    </div>
  );
};

export const SelectItem = ({ value, children, className = '' }) => {
  const context = useContext(SelectContext);
  
  if (!context) {
    throw new Error('SelectItem must be used within Select');
  }

  const { value: selectedValue, onValueChange, setOpen } = context;
  const isSelected = selectedValue === value;

  return (
    <button
      type="button"
      onClick={() => {
        onValueChange(value);
        setOpen(false);
      }}
      className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 ${
        isSelected ? 'bg-blue-100 text-blue-900 font-medium' : ''
      } ${className}`}
    >
      {children}
    </button>
  );
};
